import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { braceletGroupUpdates, cartApiUrls, clearShopifyCart, normalizeLineProperties, orphanBeadUpdates } from '@theme/cart-bracelet-cleanup';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {Promise<void> | null} */
  static #orphanCleanupPromise = null;
  /** @type {number} */
  static #removeBusyCount = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('cart-bracelet:cleanup', this.#handleBraceletCleanup);
    this.#syncRemoveBusyOverlay();
    this.#scheduleOrphanCleanup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('cart-bracelet:cleanup', this.#handleBraceletCleanup);
  }

  #handleBraceletCleanup = () => {
    this.#scheduleOrphanCleanup();
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#findCartRowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#showRemoveBusy();
    const cartItemRowToRemove = this.#findCartRowForLine(line);

    return this.#removeCartLine(Number(line), cartItemRowToRemove?.dataset?.braceletId).finally(() => {
      this.#hideRemoveBusy();
    });
  }

  #showRemoveBusy() {
    CartItemsComponent.#removeBusyCount += 1;
    this.#syncRemoveBusyOverlay();
  }

  #hideRemoveBusy() {
    CartItemsComponent.#removeBusyCount = Math.max(0, CartItemsComponent.#removeBusyCount - 1);
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof CartItemsComponent) {
        component.#syncRemoveBusyOverlay();
      }
    });
  }

  #syncRemoveBusyOverlay() {
    const overlay = this.refs.cartBusyOverlay;
    if (!(overlay instanceof HTMLElement)) return;

    const visible = CartItemsComponent.#removeBusyCount > 0;
    overlay.hidden = !visible;
    overlay.setAttribute('aria-busy', visible ? 'true' : 'false');

    const title = this.refs.cartBusyTitle;
    if (title instanceof HTMLElement && Theme?.translations?.removing_from_cart) {
      title.textContent = Theme.translations.removing_from_cart;
    }
  }

  /**
   * Finds a visible cart row by Shopify line index (1-based, includes hidden bead lines).
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #findCartRowForLine(line) {
    const match = this.querySelector(`.cart-items__table-row[data-cart-line="${line}"]`);
    return match instanceof HTMLTableRowElement ? match : undefined;
  }

  /**
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * @param {Record<string, unknown>} parsedResponseText
   * @param {number} itemCount
   */
  #publishCartRefresh(parsedResponseText, itemCount) {
    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );
  }

  /**
   * @param {Record<string, string | undefined>} [sections]
   */
  #morphCartSections(sections) {
    const sectionHtml = sections?.[this.sectionId];
    if (sectionHtml) {
      return morphSection(this.sectionId, sectionHtml, { mode: 'full' }).then(() => {
        document.dispatchEvent(new CustomEvent('cart-bracelet:refresh'));
        this.#scheduleOrphanCleanup();
      });
    }

    return sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
      document.dispatchEvent(new CustomEvent('cart-bracelet:refresh'));
      this.#scheduleOrphanCleanup();
    });
  }

  /**
   * @param {Record<string, unknown>} parsedResponseText
   */
  #readDisplayItemCount(parsedResponseText) {
    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    if (!sectionHtml) return 0;

    const newSectionHTML = new DOMParser().parseFromString(String(sectionHtml), 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    return newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
  }

  /**
   * @param {Record<string, number>} updates
   * @param {string} performanceMarker
   * @param {{ disableUi?: boolean }} [options]
   */
  #applyCartLineUpdates(updates, performanceMarker, options = {}) {
    const { disableUi = true } = options;
    const updateKeys = Object.keys(updates);
    if (!updateKeys.length) return Promise.resolve(false);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(performanceMarker);
    if (disableUi) this.#disableCartItems();

    const { cartTotal } = this.refs;
    const sectionsToUpdate = this.#getSectionsToUpdate();
    const { cartUpdateUrl } = cartApiUrls();

    cartTotal?.shimmer();

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    return fetch(cartUpdateUrl, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error(parsedResponseText.errors);
          return false;
        }

        const newCartItemCount = this.#readDisplayItemCount(parsedResponseText);
        this.#publishCartRefresh(parsedResponseText, newCartItemCount);

        return this.#morphCartSections(parsedResponseText.sections).then(() => true);
      })
      .catch((error) => {
        console.error(error);
        return false;
      })
      .finally(() => {
        if (disableUi) this.#enableCartItems();
        this.#updateCartQuantitySelectorButtonStates();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Clears the Shopify cart and refreshes cart sections.
   * @param {string} performanceMarker
   */
  #clearEntireCartAndRefresh(performanceMarker) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(performanceMarker);
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    return clearShopifyCart({
      sections: Array.from(this.#getSectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    })
      .then((parsedResponseText) => {
        resetShimmer(this);
        this.#publishCartRefresh(parsedResponseText, 0);
        return this.#morphCartSections(parsedResponseText.sections).then(() => true);
      })
      .catch((error) => {
        console.error(error);
        return false;
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Removes a visible cart line and any related hidden bracelet component lines.
   * @param {number} line
   * @param {string | undefined} braceletIdFromDom
   */
  #removeCartLine(line, braceletIdFromDom) {
    const { cartJsonUrl } = cartApiUrls();

    return fetch(cartJsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        /** @type {Record<string, number>} */
        const updates = { ...orphanBeadUpdates(cart) };

        const anchorItem = cart.items?.[line - 1];
        const anchorProps = normalizeLineProperties(anchorItem?.properties);
        const braceletId = braceletIdFromDom || anchorProps._bracelet_id;

        if (braceletId) {
          Object.assign(updates, braceletGroupUpdates(cart, String(braceletId)));
        } else if (anchorItem?.key) {
          updates[anchorItem.key] = 0;
        }

        if (Object.keys(updates).length) {
          return this.#applyCartLineUpdates(updates, 'clear:cart-line', { disableUi: false });
        }

        return this.updateQuantity(
          {
            line,
            quantity: 0,
            action: 'clear',
          },
          { disableUi: false }
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Removes orphaned hidden bead lines left after a bracelet anchor was deleted.
   */
  #cleanupOrphanBeads() {
    const { cartJsonUrl } = cartApiUrls();

    return fetch(cartJsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (this.dataset.displayEmpty === 'true' && cart.items?.length) {
          return this.#clearEntireCartAndRefresh('cleanup:clear-stale-cart');
        }

        const updates = orphanBeadUpdates(cart);
        if (!Object.keys(updates).length) return false;

        return this.#applyCartLineUpdates(updates, 'cleanup:orphan-beads', { disableUi: false }).then(() => true);
      });
  }

  /**
   * Runs orphan cleanup (debounced; safe to call after every cart mutation).
   */
  #scheduleOrphanCleanup() {
    if (CartItemsComponent.#orphanCleanupPromise) {
      return CartItemsComponent.#orphanCleanupPromise;
    }

    CartItemsComponent.#orphanCleanupPromise = this.#cleanupOrphanBeads()
      .then((didUpdate) => {
        if (didUpdate) {
          return this.#cleanupOrphanBeads();
        }
      })
      .finally(() => {
        CartItemsComponent.#orphanCleanupPromise = null;
      });

    return CartItemsComponent.#orphanCleanupPromise;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {{ disableUi?: boolean }} [options]
   */
  updateQuantity(config, options = {}) {
    const { disableUi = true } = options;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    if (disableUi) this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newCartItemCount = this.#readDisplayItemCount(parsedResponseText);
        this.#publishCartRefresh(parsedResponseText, newCartItemCount);

        this.#morphCartSections(parsedResponseText.sections);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (disableUi) this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, { mode: 'full' }).then(() => {
        document.dispatchEvent(new CustomEvent('cart-bracelet:refresh'));
        this.#scheduleOrphanCleanup();
      });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
